/**
 * Theme (docs/14 "Theme contexts"): `:root` light is the portal default; `.dark` on <html> by
 * user preference, ONLY while the portal (or onboarding) is shown; `.theme-oex` is applied by the
 * admin, lobby, sign-in and public layouts on their own wrapper element (`<OexArea>`), never
 * product-wide.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@oex/ui';

export type ThemePreference = 'light' | 'dark' | 'system';
type ThemeApi = { preference: ThemePreference; setPreference(p: ThemePreference): void };

const KEY = 'oex.theme';
const ThemeContext = createContext<ThemeApi>({ preference: 'light', setPreference: () => {} });

function stored(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: ThemePreference | null;
}) {
  const [preference, setPref] = useState<ThemePreference>(() => initial ?? stored());
  useEffect(() => {
    if (initial) setPref(initial);
  }, [initial]);
  const setPreference = useCallback((p: ThemePreference) => {
    setPref(p);
    try {
      localStorage.setItem(KEY, p);
    } catch {
      // ignore
    }
  }, []);
  const value = useMemo(() => ({ preference, setPreference }), [preference, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  return useContext(ThemeContext);
}

/** Portal and onboarding: honour the user's light/dark preference on <html>. */
export function usePortalTheme(): void {
  const { preference } = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    const media =
      typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const apply = () =>
      root.classList.toggle('dark', preference === 'dark' || (preference === 'system' && !!media?.matches));
    apply();
    media?.addEventListener?.('change', apply);
    return () => {
      media?.removeEventListener?.('change', apply);
      root.classList.remove('dark');
    };
  }, [preference]);
}

/** Branded areas (admin, lobby, sign-in, public): `.theme-oex` on this wrapper only. */
export function OexArea({
  children,
  className,
  grid,
}: {
  children: ReactNode;
  className?: string;
  grid?: boolean;
}) {
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);
  return (
    <div
      data-theme-area="oex"
      className={cn('theme-oex min-h-dvh bg-background text-foreground', grid && 'grid-lines', className)}
    >
      {children}
    </div>
  );
}
